import { useEffect, useState } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Chip,
    Grid,
    Menu,
    MenuItem,
    Stack,
    Button,
    Alert,
    Typography
} from '@mui/material';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import SectionHeader from '../../components/SectionHeader';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CircularProgress } from '@mui/material';
import { PeriodicosFilters } from './components/PeriodicosFilters';
import PeriodicosDrawerManager, {
    PERIODICOS_DRAWER_ACTION_VALUES
} from './components/PeriodicosDrawerManager';
import ExposedGrid from './components/ExposedGrid';
import { usePeriodicos } from './hooks/usePeriodicos';
import { useRoles } from '../../hooks/useRoles';
import { useLocation } from 'react-router-dom';

const ExpuestosIndex = ({ reconfirmation = false }: { reconfirmation?: boolean }) => {
    const { t } = useTranslation('periodicos');
    const { isAuditor } = useRoles();
    const { isMobile, isTablet } = useIsMobile();
    const [isTabLoading, setIsTabLoading] = useState(false);

    const location = useLocation();
    const selectedEstablishment = location.state?.selectedEstablishment || null;

    const [showGrid, setShowGrid] = useState(false);
    const {
        handleClickAction,
        selectedIds,
        employeesTableMenu,
        isFetching,
        selectedEmployee,
        tabIndex,
        open,
        setOpen,
        handleSelectEmployee,
        handleTabChange,
        handleAsignarPrestador,
        handleAsignarExamen,
        handleGenerarConvocatorias,
        handleExportarExcel,
        handleClearFilters,
        reloadGrid,
        handleSubmitSearch,
        handleChangeExtraFilters,
        extraFilters,
        filters,
        setFilters,
        showRiesgoError,
        handleSelectAllEmployed,
        fetchData,
        getUniqueIdByTabIndex,
        getColumnWidths,
        handleSearchAndClearSelection,
        setTabIndex
    } = usePeriodicos({ reconfirmation });

    const { handleCloseCard, closeMenu, openCard, anchorEl, menuOpen } = employeesTableMenu;
    const translate = (key: string): string => t(key) as string;
    useEffect(() => {
        if (extraFilters?.idEstado !== undefined) {
            handleSubmitSearch();
        }
    }, [extraFilters?.idEstado]);

    const reloadCurrentTab = async () => {
        await handleSubmitSearch();
    };

    useEffect(() => {
        if (isAuditor) {
            setTabIndex(reconfirmation ? 0 : 3);
        } else {
            setTabIndex(0);
        }
        handleSubmitSearch();
    }, [reconfirmation, isAuditor]);

    const headerTitle = reconfirmation
        ? t('header.title.reconfirmation')
        : t('header.title.exposed');

    useEffect(() => {
        setIsTabLoading(true);
    }, []);

    const onTabChange = async (_: React.SyntheticEvent, newValue: number) => {
        if (newValue === tabIndex) {
            await reloadCurrentTab();
            return;
        }

        setShowGrid(false);
        setIsTabLoading(true);
        handleTabChange(_, newValue);
        await handleSubmitSearch();
    };

    useEffect(() => {
        if (!isFetching) {
            setIsTabLoading(false);
            setShowGrid(true);
        }
    }, [isFetching]);

    const renderActionButtons = () => {
        if (isAuditor) return null;

        switch (tabIndex) {
            case 0:
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={
                            selectedIds.length === 0 || selectedIds.length < 2 || showRiesgoError
                        }
                        onClick={handleAsignarExamen}
                    >
                        {t('actions.assignExam')}
                    </Button>
                );
            case 1:
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedIds.length === 0}
                        onClick={handleAsignarPrestador}
                    >
                        {t('actions.assignPrestador')}
                    </Button>
                );
            case 2:
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedIds.length === 0}
                        onClick={handleGenerarConvocatorias}
                    >
                        {t('actions.generateCall')}
                    </Button>
                );
            case 3:
                return (
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" color="primary" onClick={handleExportarExcel}>
                            {t('actions.exportExcel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={selectedIds.length === 0}
                            onClick={() => {
                                closeMenu();
                                setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_SHIFT);
                            }}
                        >
                            {t('actions.assigmentTurns')}
                        </Button>
                    </Stack>
                );

            default:
                return null;
        }
    };

    const effectiveTabIndex = (() => {
        if (isAuditor) {
            if (reconfirmation) {
                return tabIndex === 0 ? 0 : 3;
            }
            return 3;
        }
        return tabIndex;
    })();

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <SectionHeader>
                        <SectionHeader.Title>{headerTitle}</SectionHeader.Title>
                    </SectionHeader>
                </Grid>
                <Grid item xs={12}>
                    <SearchToolbar
                        inputSearchName=""
                        onChange={() => {}}
                        onClick={handleSearchAndClearSelection}
                        showSearchInput={false}
                        clearFilters={handleClearFilters}
                    >
                        <PeriodicosFilters
                            extraFilters={extraFilters}
                            handleChangeExtraFilters={handleChangeExtraFilters}
                            tabIndex={tabIndex}
                            reconfirmation={reconfirmation}
                            handleSubmitSearch={handleSubmitSearch}
                            setFilters={setFilters}
                            selectedEstablishmentId={selectedEstablishment?.id || null}
                            location={location}
                        />
                    </SearchToolbar>
                </Grid>
            </Grid>

            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                spacing={3}
            >
                {openCard && (isMobile || isTablet) ? (
                    ''
                ) : (
                    <Grid item xs={openCard ? 6 : 12} lg={openCard ? 8 : 12}>
                        <Box sx={{ mb: 2 }}>
                            {!isAuditor && (
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mb: 2 }}
                                >
                                    {
                                        <Grid item>
                                            <Tabs
                                                value={tabIndex}
                                                onChange={onTabChange}
                                                sx={{ height: 64, paddingBottom: '24px' }}
                                            >
                                                <Tab
                                                    label={t('table.tabs.pendingExamn')}
                                                    onClick={() =>
                                                        tabIndex === 0 && reloadCurrentTab()
                                                    }
                                                    sx={{
                                                        fontSize: '1rem',
                                                        fontWeight: 'bold',
                                                        px: 4,
                                                        py: 2
                                                    }}
                                                />
                                                <Tab
                                                    label={t('table.tabs.pendingPrestador')}
                                                    onClick={() =>
                                                        tabIndex === 1 && reloadCurrentTab()
                                                    }
                                                    sx={{
                                                        fontSize: '1rem',
                                                        fontWeight: 'bold',
                                                        px: 4,
                                                        py: 2
                                                    }}
                                                />
                                                <Tab
                                                    label={t('table.tabs.pendingCall')}
                                                    onClick={() =>
                                                        tabIndex === 2 && reloadCurrentTab()
                                                    }
                                                    sx={{
                                                        fontSize: '1rem',
                                                        fontWeight: 'bold',
                                                        px: 4,
                                                        py: 2
                                                    }}
                                                />
                                                <Tab
                                                    label={t('table.tabs.called')}
                                                    onClick={() =>
                                                        tabIndex === 3 && reloadCurrentTab()
                                                    }
                                                    sx={{
                                                        fontSize: '1rem',
                                                        fontWeight: 'bold',
                                                        px: 4,
                                                        py: 2
                                                    }}
                                                />
                                            </Tabs>
                                        </Grid>
                                    }
                                    <Grid item>{renderActionButtons()}</Grid>
                                </Grid>
                            )}

                            {isAuditor && (
                                <Tabs
                                    value={tabIndex}
                                    onChange={onTabChange}
                                    sx={{
                                        height: 64,
                                        paddingBottom: '24px',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {reconfirmation && (
                                        <Tab
                                            label={t('table.tabs.pendingExamn')}
                                            value={0}
                                            onClick={() => tabIndex === 0 && reloadCurrentTab()}
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                px: 4,
                                                py: 2
                                            }}
                                        />
                                    )}

                                    <Tab
                                        label={t('table.tabs.called')}
                                        value={reconfirmation ? 1 : 3}
                                        onClick={() =>
                                            tabIndex === (reconfirmation ? 1 : 3) &&
                                            reloadCurrentTab()
                                        }
                                        sx={{
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            px: 4,
                                            py: 2
                                        }}
                                    />
                                </Tabs>
                            )}

                            {isTabLoading ? (
                                <Box display="flex" justifyContent="center" mt={4}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <ExposedGrid
                                    tabIndex={effectiveTabIndex}
                                    items={fetchData}
                                    filters={filters}
                                    setFilters={setFilters}
                                    isFetching={isFetching}
                                    selectedEmployee={selectedEmployee}
                                    selectedIds={selectedIds}
                                    handleSelectEmployee={handleSelectEmployee}
                                    handleClickAction={handleClickAction}
                                    showRiesgoError={showRiesgoError}
                                    handleSelectAllEmployed={handleSelectAllEmployed}
                                    getUniqueIdByTabIndex={getUniqueIdByTabIndex}
                                    getColumnWidths={getColumnWidths}
                                />
                            )}
                        </Box>
                    </Grid>
                )}
            </Grid>

            <Menu id="basic-menu" anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
                <Box>
                    {effectiveTabIndex === 0 && (
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                handleAsignarExamen();
                            }}
                        >
                            {t('table.actions.assignmentExam')}
                        </MenuItem>
                    )}

                    {effectiveTabIndex === 1 && (
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                setOpen(PERIODICOS_DRAWER_ACTION_VALUES.OLD_ASSIGN_PRESTADOR);
                            }}
                        >
                            {t('table.actions.assignmentPrestador')}
                        </MenuItem>
                    )}

                    {effectiveTabIndex === 2 && (
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_EXAM_AND_PRESTADOR);
                            }}
                        >
                            {t('table.actions.seeDetails')}
                        </MenuItem>
                    )}

                    {effectiveTabIndex === 3 &&
                        [2, 3, 4, 5].includes(
                            selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto ||
                                selectedEmployee?.estadoEmpleadoExpuesto
                        ) && (
                            <MenuItem
                                onClick={() => {
                                    closeMenu();
                                    setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_SHIFT);
                                }}
                            >
                                {t('table.actions.seeTurn')}
                            </MenuItem>
                        )}

                    {effectiveTabIndex === 3 &&
                        [4, 5, 6].includes(
                            selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto ||
                                selectedEmployee?.estadoEmpleadoExpuesto
                        ) && (
                            <MenuItem
                                onClick={() => {
                                    closeMenu();
                                    setOpen(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_RESULTS);
                                }}
                            >
                                {t('table.actions.seeResults')}
                            </MenuItem>
                        )}
                </Box>
            </Menu>

            <PeriodicosDrawerManager
                open={open}
                setOpen={setOpen}
                selectedIds={selectedIds}
                reloadGrid={reloadGrid}
                selectedEmployee={selectedEmployee}
                reloadCurrentTab={reloadCurrentTab}
                tabIndex={isAuditor ? (reconfirmation ? tabIndex : 3) : tabIndex}
                reconfirmation={reconfirmation}
            />
        </>
    );
};

export default ExpuestosIndex;
